import { gettext } from '@eventbrite/i18n';
import classnames from 'classnames';
import React, { ReactNode, useEffect, useRef, useState } from 'react';

import {
    getNextItem,
    getPrevItem,
    getSlidesTranslationValue,
    getSlideTranslateX,
    isLastItem,
} from './helpers';
import { useKeyPress } from './useKeyPress';
import { useSwipe } from './useSwipe';

import './CarouselSwiper.scss';

export type CarouselSwiperControllerProps = {
    initialSlide: number;
    items: ReactNode[];
};

export const useCarouselSwiperController = ({
    initialSlide = 0,
    items,
}: CarouselSwiperControllerProps) => {
    const [currentIndex, setCurrentIndex] = useState(initialSlide);

    return {
        currentIndex,
        setCurrentIndex,
        isLastSlide: isLastItem(currentIndex, items),
        isFirstSlide: currentIndex === 0,
        prevSlide: getPrevItem(currentIndex, items),
        nextSlide: getNextItem(currentIndex, items),
    };
};
export interface CarouselSwiperProps {
    className?: string;
    currentIndex: number;
    enableNavigationLooping?: boolean;
    gap?: string;
    items: ReactNode[];
    setCurrentIndex: (index: number) => void;
    controls?: ReactNode;
    progressClassName?: string;
    viewportClassName?: string;
}

export const CarouselSwiper: React.FC<CarouselSwiperProps> = ({
    className,
    currentIndex,
    enableNavigationLooping = false,
    gap = '',
    items,
    controls,
    progressClassName,
    setCurrentIndex,
    viewportClassName,
}) => {
    const styles = { '--gap': gap } as React.CSSProperties;
    const firstSlide = 0;
    const lastSlide = items.length - 1;

    const [translationUnit, setTranslationUnit] = useState<string>(
        getSlidesTranslationValue(currentIndex),
    );
    const [isCarouselFocused, setIsCarouselFocused] = useState(false);
    const shouldHideLeftControl =
        currentIndex === firstSlide && !enableNavigationLooping;
    const shouldHideRightControl =
        currentIndex === lastSlide && !enableNavigationLooping;
    const carousel = useRef(null);

    useEffect(() => {
        setTranslationUnit(getSlidesTranslationValue(currentIndex));
    }, [currentIndex]);

    const nextSlide = () => {
        const nextSlide = getNextItem(currentIndex, items);
        setCurrentIndex(nextSlide);
    };

    const prevSlide = () => {
        const prevSlide = getPrevItem(currentIndex, items);
        setCurrentIndex(prevSlide);
    };

    const handleOnFocus = () => {
        setIsCarouselFocused(true);
    };

    const handleOnBlur = (event: React.FocusEvent<HTMLDivElement>) => {
        const isCarouselBlurred = !event.currentTarget.contains(
            event.relatedTarget as Node,
        );
        if (isCarouselBlurred) {
            setIsCarouselFocused(false);
        }
    };

    const shouldDisableLeftNavigation =
        !enableNavigationLooping && shouldHideLeftControl;
    const shouldDisableRightNavigation =
        !enableNavigationLooping && shouldHideRightControl;

    useKeyPress({
        isCarouselFocused,
        onLeftKeyPressed: () => prevSlide(),
        onRightKeyPressed: () => nextSlide(),
        shouldDisableLeftNavigation,
        shouldDisableRightNavigation,
    });

    useSwipe({
        ref: carousel,
        onSwipeLeft: () => nextSlide(),
        onSwipeRight: () => prevSlide(),
        shouldDisableLeftNavigation,
        shouldDisableRightNavigation,
    });

    const progressItems = items.map((_, index: number) => {
        const isCurrentSlide = index === currentIndex;
        return (
            <li key={index} data-testid="eds-carousel-swiper-progress-item">
                <button
                    className={classnames(
                        'eds-carousel-swiper-progress-list-item-button',
                        {
                            'eds-carousel-swiper-progress-list-item-button-selected':
                                isCurrentSlide,
                        },
                    )}
                    onClick={() => setCurrentIndex(index)}
                    disabled={isCurrentSlide}
                    aria-current={isCurrentSlide}
                    aria-label={gettext('Go to slide %(slideNumber)s', {
                        slideNumber: index + 1,
                    }).toString()}
                    type="button"
                />
            </li>
        );
    });

    const viewportItems = items.map((item, index: number) => (
        <li
            className="eds-carousel-swiper-viewport-list-item"
            data-testid="slide"
            aria-hidden={index !== firstSlide}
            key={`slide-${index}`}
            onClick={() =>
                [currentIndex - 1, currentIndex + 1].includes(index)
                    ? setCurrentIndex(index)
                    : null
            }
        >
            <div>{item}</div>
        </li>
    ));

    return (
        <div
            ref={carousel}
            tabIndex={0}
            onFocus={handleOnFocus}
            onBlur={handleOnBlur}
            data-testid={'carousel-swiper'}
            className={classnames('eds-carousel-swiper-wrapper', className)}
            style={styles}
        >
            {controls}
            <div
                className={classnames(
                    'eds-carousel-swiper-progress',
                    progressClassName,
                )}
            >
                <ul className="eds-carousel-swiper-progress-list">
                    {progressItems}
                </ul>
            </div>

            <div className="eds-carousel-swiper-viewport">
                <ul
                    className={classnames(
                        'eds-carousel-swiper-viewport-list',
                        viewportClassName,
                    )}
                    style={{
                        transform: getSlideTranslateX({
                            gap,
                            slidePosition: currentIndex,
                            translationValue: translationUnit,
                        }),
                    }}
                    data-testid="slide-list"
                >
                    {viewportItems}
                </ul>
            </div>
        </div>
    );
};
